import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import axios from 'axios';
import PropTypes from 'prop-types';
import { RESET_SUCCESS_MESSAGE } from '../../actions/action_parent';
import MyRegistrations from '../event-registration/MyRegistrations';
import {
  fetchVoucherProducts,
  fetchVouchers,
} from '../../actions/action_vouchers';
import { formatAddressLines } from '../../utils/format_address';

const LeftColumn = styled.div`
  padding: 0 15px;
`;
const RightColumn = styled.div`
  padding: 0 15px;
  float: right;
`;
const Wrapper = styled.div`
  margin-top: 0;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px 24px 24px;
  margin-bottom: 32px;
`;

const MainHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const Divider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const Header1 = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #479de6;
`;

const EditLinkStyled = styled(Link)`
  text-decoration: underline;
  color: #479de6;
  cursor: pointer;
  margin-top: 16px;
`;

const SuccessAlert = styled.div`
  margin-top: 16px;
`;

const ResubscribeButton = styled.button`
  background: none;
  border: none;
  color: #479de6;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font: inherit;
`;

const Label = styled.span`
  font-weight: bold;
  display: block;
  margin-bottom: 4px;
`;

const VoucherSummaryCard = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 16px;
  color: #333;
  margin-bottom: 16px;
`;

const VoucherTotalBalance = styled.div`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #16a34a;
`;

const VoucherBalanceLabel = styled.div`
  font-size: 12px;
  color: #666;
`;

const VoucherBreakdown = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #bbf7d0;
  font-size: 13px;
`;

const VoucherBreakdownItem = styled.div`
  span:first-child {
    color: #666;
  }
  span:last-child {
    font-weight: 600;
    margin-left: 4px;
    color: #16a34a;
  }
`;

const VoucherExpiryWarning = styled.div`
  background: #fef3c7;
  color: #92400e;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const ViewAllLink = styled(Link)`
  display: block;
  text-align: center;
  color: #479de6;
  font-size: 14px;
  margin-top: 12px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ActionCard = styled.div`
  background: linear-gradient(135deg, #479de6 0%, #3b8bd4 100%);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  color: #fff;
  box-shadow: 0px 4px 12px rgba(71, 157, 230, 0.3);
`;

const ActionCardTitle = styled.h4`
  color: #fff;
  margin: 0 0 8px 0;
  font-size: 20px;
`;

const ActionCardText = styled.p`
  font-size: 14px;
  margin-bottom: 16px;
  opacity: 0.9;
`;

const ActionCardButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: #479de6;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background-color: #f0f7ff;
    color: #3b8bd4;
  }
`;

const EmailPreferences = ({ parent }) => {
  const [unsubscribeStatus, setUnsubscribeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const email = parent.email;
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchUnsubscribeStatus = async () => {
      try {
        const response = await axios.get(
          `/api/v2/parents/unsubscribe-status?email=${encodeURIComponent(
            email,
          )}`,
        );
        setUnsubscribeStatus(response.data);
      } catch (err) {
        setError('Failed to load email preferences');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUnsubscribeStatus();
  }, [parent]);

  const handleResubscribe = async (eventId = null) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/v2/parents/resubscribe-email', {
        email,
        eventId,
        all: !eventId,
      });

      dispatch({
        type: 'SET_UNSUBSCRIBE_STATUS',
        payload: response.data,
      });
    } catch (err) {
      setError('Failed to resubscribe');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p>Loading email preferences...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!unsubscribeStatus) return null;

  let content;
  if (unsubscribeStatus.all_events) {
    content = (
      <div>
        <p>You are currently unsubscribed from all email communications.</p>
        <ResubscribeButton onClick={() => handleResubscribe()}>
          Resubscribe to all emails
        </ResubscribeButton>
      </div>
    );
  } else if (
    unsubscribeStatus.event_unsubscribes &&
    unsubscribeStatus.event_unsubscribes.length > 0
  ) {
    content = (
      <div>
        <p>You are unsubscribed from emails for the following events:</p>
        <ul>
          {unsubscribeStatus.event_unsubscribes.map((event) => (
            <li key={event.id}>
              {event.name}{' '}
              <ResubscribeButton onClick={() => handleResubscribe(event.id)}>
                Resubscribe
              </ResubscribeButton>
            </li>
          ))}
        </ul>
      </div>
    );
  } else {
    content = <p>You are currently subscribed to all email communications.</p>;
  }

  return (
    <div>
      <Header1>Email Preferences</Header1>
      <hr />
      {content}
    </div>
  );
};

const SMSPreferences = ({ phone, email }) => {
  const [unsubscribeStatus, setUnsubscribeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUnsubscribeStatus = async () => {
      try {
        const response = await axios.get(
          `/api/v2/parents/unsubscribe-status?email=${encodeURIComponent(
            email,
          )}`,
        );
        setUnsubscribeStatus(response.data);
      } catch (err) {
        setError('Failed to load SMS preferences');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUnsubscribeStatus();
  }, [email]);

  if (loading) return <p>Loading SMS preferences...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!unsubscribeStatus) return null;

  const formatPhoneNumber = (phoneStr) => {
    const cleaned = phoneStr.replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return ['(', match[2], ') ', match[3], '-', match[4]].join('');
    }
    return phoneStr;
  };

  let content;
  if (unsubscribeStatus.sms_unsubscribed) {
    const formattedFromNumber = unsubscribeStatus.sms_to_number
      ? formatPhoneNumber(unsubscribeStatus.sms_to_number)
      : 'the number you received messages from';

    content = (
      <div>
        <p>
          You have unsubscribed from SMS notifications for the number{' '}
          {formatPhoneNumber(phone)}.
        </p>
        <p>
          To resubscribe to SMS notifications, text <strong>START</strong> to
          <br />
          {formattedFromNumber}.
        </p>
      </div>
    );
  } else {
    content = <p>You are currently subscribed to SMS notifications.</p>;
  }

  return (
    <div>
      <Header1>SMS Preferences</Header1>
      <hr />
      {content}
    </div>
  );
};

SMSPreferences.propTypes = {
  phone: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

const formatVoucherDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

const ParentMyAccount = () => {
  const dispatch = useDispatch();
  const parent = useSelector((state) => state.parent);
  const successMessage = useSelector((state) => state.parent.successMessage);
  const vouchersAvailable = useSelector(
    (state) => state.vouchers.vouchersAvailable,
  );
  const vouchers = useSelector((state) => state.vouchers.vouchers);
  const vouchersLoading = useSelector(
    (state) => state.vouchers.vouchersLoading,
  );
  const [parentData, setParentData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only show vouchers section if customer has purchased vouchers
  // (not just if vouchers are available for purchase)
  const hasVouchers = vouchers && vouchers.length > 0;
  const showVouchers = hasVouchers;

  useEffect(() => {
    const fetchParentData = async () => {
      try {
        const response = await axios.get('/api/v2/parents/get_parent_data');
        setParentData(response.data);
      } catch (err) {
        console.error('Failed to fetch parent data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchParentData();
    dispatch(fetchVoucherProducts());
    dispatch(fetchVouchers());
  }, []);

  const formattedPhoneNumber = parsePhoneNumberFromString(
    parent.smsPhone || parent.phone,
    'US',
  );

  useEffect(
    () => () => {
      dispatch({ type: RESET_SUCCESS_MESSAGE });
    },
    [successMessage],
  );

  if (loading) return <p>Loading...</p>;

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <MainHeader>Account Information</MainHeader>
        {successMessage && (
          <SuccessAlert className="alert alert-success">
            <p>
              {successMessage}{' '}
              <EditLinkStyled to="/dashboard">Start an order</EditLinkStyled>
            </p>
          </SuccessAlert>
        )}
        <Divider />
        <div className="row">
          <LeftColumn className="col-xs-12 col-md-7 col-lg-7">
            <Wrapper>
              <Header1>Profile and Contact</Header1>
              <hr />
              <div>
                <Label id="name-label">Name</Label>
                <p aria-labelledby="name-label">
                  {parent.firstName} {parent.lastName}
                </p>
                <EditLinkStyled to="/my-account/name">edit name</EditLinkStyled>
              </div>
              <hr />
              {parent.authProvider ? (
                <div>
                  <Label id="email-label">
                    Email (via{' '}
                    {parent.authProvider.charAt(0).toUpperCase() +
                      parent.authProvider.slice(1)}
                    )
                  </Label>
                  <p aria-labelledby="email-label">{parent.email}</p>
                </div>
              ) : (
                <div>
                  <Label id="email-label">Email</Label>
                  <p aria-labelledby="email-label">{parent.email}</p>
                  {parent.unconfirmedEmail ? (
                    <>
                      <hr />
                      <Label id="unconfirmed-email-label">
                        Awaiting confirmation
                      </Label>
                      <p aria-labelledby="unconfirmed-email-label">
                        {parent.unconfirmedEmail} (check your email for a link)
                      </p>
                      <EditLinkStyled to="/my-account/email">
                        edit email or resend confirmation
                      </EditLinkStyled>
                    </>
                  ) : (
                    <EditLinkStyled to="/my-account/email">
                      edit email
                    </EditLinkStyled>
                  )}
                  <hr />
                  <EditLinkStyled to="/my-account/change-password">
                    reset password
                  </EditLinkStyled>
                </div>
              )}
              <hr />
              <div>
                <Label id="phone-label">Phone</Label>
                <p aria-labelledby="phone-label">
                  {formattedPhoneNumber
                    ? formattedPhoneNumber.formatNational()
                    : 'Invalid Number'}
                </p>
                <EditLinkStyled to="/my-account/phone">
                  edit phone
                </EditLinkStyled>
              </div>
              <hr />
              <div>
                <Label id="address-label">Address</Label>
                <p aria-labelledby="address-label">
                  {formatAddressLines(parent.address).map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
                <EditLinkStyled to="/my-account/address">
                  edit address
                </EditLinkStyled>
              </div>
            </Wrapper>
            <Wrapper>
              <Header1>Order History</Header1>
              <hr />
              <p style={{ color: '#666', marginBottom: '16px' }}>
                View your past orders and order details.
              </p>
              <Link
                to="/my-orders"
                className="btn btn-primary"
                style={{
                  backgroundColor: '#479de6',
                  border: 'none',
                  width: '100%',
                  padding: '12px',
                  fontWeight: '600',
                  display: 'block',
                  textAlign: 'center',
                }}
              >
                View My Orders
              </Link>
            </Wrapper>
          </LeftColumn>
          <RightColumn className="col-xs-12 col-md-5 col-lg-5">
            {showVouchers && vouchers.length > 0 && (
              <ActionCard>
                <ActionCardTitle>Ready to order?</ActionCardTitle>
                <ActionCardText>
                  Use your voucher credit on your next purchase.
                </ActionCardText>
                <ActionCardButton to="/dashboard">
                  Start an Order
                </ActionCardButton>
              </ActionCard>
            )}
            <Wrapper>
              <Header1>Students</Header1>
              <hr />
              <div>
                {parent.students.map((student) => (
                  <div key={`${student.id}`} style={{ marginBottom: '8px' }}>
                    <Link
                      to={`/my-account/students/${student.id}`}
                      style={{ color: '#479de6', textDecoration: 'none' }}
                    >
                      {student.firstName} {student.lastName}
                    </Link>
                  </div>
                ))}
              </div>
              <Link
                to="/my-account/add-student"
                className="btn btn-sm btn-primary"
                style={{ marginTop: '16px' }}
              >
                Add Student
              </Link>
            </Wrapper>
            {showVouchers && (
              <Wrapper>
                <Header1>Vouchers</Header1>
                <hr />
                {vouchersLoading ? (
                  <p>Loading vouchers...</p>
                ) : vouchers.length > 0 ? (
                  <>
                    <VoucherSummaryCard>
                      <VoucherBalanceLabel>Total Balance</VoucherBalanceLabel>
                      <VoucherTotalBalance>
                        {formatCurrency(
                          vouchers.reduce(
                            (sum, v) => sum + (v.total_balance_cents || 0),
                            0,
                          ),
                        )}
                      </VoucherTotalBalance>
                      <VoucherBreakdown>
                        <VoucherBreakdownItem>
                          <span>Purchased:</span>
                          <span>
                            {formatCurrency(
                              vouchers.reduce(
                                (sum, v) =>
                                  sum + (v.purchased_balance_cents || 0),
                                0,
                              ),
                            )}
                          </span>
                        </VoucherBreakdownItem>
                        <VoucherBreakdownItem>
                          <span>Promo:</span>
                          <span>
                            {formatCurrency(
                              vouchers.reduce(
                                (sum, v) => sum + (v.promo_balance_cents || 0),
                                0,
                              ),
                            )}
                          </span>
                        </VoucherBreakdownItem>
                      </VoucherBreakdown>
                    </VoucherSummaryCard>
                    {(() => {
                      const expiringVouchers = vouchers.filter(
                        (v) =>
                          v.promo_balance_cents > 0 &&
                          v.promo_expires_at &&
                          !v.promo_expired &&
                          v.days_until_promo_expiration <= 30,
                      );
                      if (expiringVouchers.length === 0) return null;
                      const soonest = expiringVouchers.sort(
                        (a, b) =>
                          a.days_until_promo_expiration -
                          b.days_until_promo_expiration,
                      )[0];
                      const totalExpiring = expiringVouchers.reduce(
                        (sum, v) => sum + v.promo_balance_cents,
                        0,
                      );
                      return (
                        <VoucherExpiryWarning>
                          {formatCurrency(totalExpiring)} promo credit expires{' '}
                          {formatVoucherDate(soonest.promo_expires_at)}
                          {soonest.days_until_promo_expiration <= 7 &&
                            ` (${soonest.days_until_promo_expiration} days)`}
                        </VoucherExpiryWarning>
                      );
                    })()}
                  </>
                ) : (
                  <p style={{ color: '#666', marginBottom: '16px' }}>
                    You don&apos;t have any vouchers yet.
                  </p>
                )}
                {/* TODO: Re-enable when voucher purchase flow is ready for direct purchase
                <Link
                  to="/vouchers/purchase"
                  className="btn btn-primary"
                  style={{
                    backgroundColor: '#22c55e',
                    border: 'none',
                    width: '100%',
                    padding: '12px',
                    fontWeight: '600',
                    display: 'block',
                    textAlign: 'center',
                  }}
                >
                  Buy Vouchers
                </Link>
                */}
                {vouchers.length > 0 && (
                  <ViewAllLink to="/my-account/vouchers">
                    View all voucher details &rarr;
                  </ViewAllLink>
                )}
              </Wrapper>
            )}
            <Wrapper>
              <MyRegistrations />
            </Wrapper>
            <Wrapper>
              <EmailPreferences parent={parent} />
            </Wrapper>
            {parentData && parentData.sms_marketing_enabled && (
              <Wrapper>
                <SMSPreferences
                  phone={
                    formattedPhoneNumber
                      ? formattedPhoneNumber.formatNational()
                      : parent.smsPhone || parent.phone
                  }
                  email={parent.email}
                />
              </Wrapper>
            )}
          </RightColumn>
        </div>
      </div>
    </div>
  );
};

export default ParentMyAccount;
